import { SizeType } from '@goldcast/player-config/src/types/asset-types';
import { TEXT_COLOR_DEFAULT } from '@goldcast/player-config/src/constants/colors';
import { VideoElement } from '@goldcast/player-config/src/types/RemotionPlayerConfigTypes/Element/VideoElement';
import { SpeakerWithDetails } from '@goldcast/player-config/src/types/speaker-types';
import { getTruncatedName, getTruncatedTitle } from '@/Pages/Clip/CompositePlayer/SpeakerVideoUtils';
import Icon from '@/components/atoms/Icon';

// Safe avatar — AudiogramEmptyProfilePicture uses getRemotionEnvironment() + useRemotionUsage()
// which crash outside Remotion.
function SpeakerAvatar({ imgUrl }: { imgUrl: string | null }) {
  if (imgUrl) {
    return <img className="block size-full object-cover" src={imgUrl} alt="" />;
  }
  return (
    <div className="flex size-full items-center justify-center bg-slate-200">
      <Icon name="IconPhoto" size={'0.6rem'} />
    </div>
  );
}

// Static waveform — pill-shaped bars, bell-curve heights, centered on x-axis
// Matches the RemotionWaveform appearance: tallest in the center, shortest at the edges
function StaticWaveform({ color }: { color: string }) {
  const bars = [0.3, 0.8, 0.55, 0.2, 0.3, 0.5, 0.6, 0.35, 0.2];
  return (
    <div className="flex h-full items-center justify-center" style={{ gap: '0.12em' }}>
      {bars.map((scale, i) => (
        <div key={i} style={{ height: `${scale * 100}%`, width: '0.12em', backgroundColor: color }} />
      ))}
    </div>
  );
}

export default function AudiogramLayoutPreview({
  size,
  video,
  speakerDetailsFallback,
  textColor
}: {
  size: SizeType;
  video: VideoElement | null;
  speakerDetailsFallback?: SpeakerWithDetails;
  textColor?: string;
}) {
  // VideoElement.speakerDetails is only populated when the clip is in audiogram layout.
  // Use speakerDetailsFallback (from VideoFeed) when previewing audiogram with a different clip layout.
  const speakerDetails = video?.speakerDetails ?? speakerDetailsFallback;
  if (!speakerDetails) {
    return null;
  }

  const { profile_picture_url, first_name = '', last_name = '' } = speakerDetails;
  const imgUrl = profile_picture_url ?? null;

  // Waveform color: exact match to AudiogramComponent (video.speakerLabel?.style?.color ?? TEXT_COLOR_DEFAULT)
  // speakerLabel.style.color is generated from magicLayout.textColor by getSpeakerLabelConfigForAudiograms.
  // When speakerLabel is absent (clip not in audiogram layout), fall back to textColor directly.
  const waveColor = (video?.speakerLabel?.style?.color as string) ?? textColor ?? TEXT_COLOR_DEFAULT;
  // Element styles have no color — it's set on the container and inherited via CSS.
  // Use the container color (same as waveColor) for all text.
  const nameColor = waveColor;
  const titleColor = waveColor;

  const speakerName = getTruncatedName(`${first_name} ${last_name}`.trim(), 'AUDIOGRAM', size);
  const speakerTitle = getTruncatedTitle(speakerDetails, 'AUDIOGRAM', size);

  if (size === 'LANDSCAPE') {
    return (
      <div className="flex size-full">
        <div className="h-full w-1/2 overflow-hidden">
          <SpeakerAvatar imgUrl={imgUrl} />
        </div>
        <div className="flex flex-1 items-end justify-between p-[2%]">
          <div className="flex flex-1 flex-col">
            <div className="truncate leading-tight" style={{ fontSize: '1.1em', color: nameColor }}>
              {speakerName}
            </div>
            {speakerTitle && (
              <div className="truncate leading-tight opacity-70" style={{ fontSize: '0.7em', color: titleColor }}>
                {speakerTitle}
              </div>
            )}
          </div>
          <div className="h-1/5">
            <StaticWaveform color={waveColor} />
          </div>
        </div>
      </div>
    );
  }

  if (size === 'SQUARE') {
    // Mirrors AudiogramComponent SQUARE: image is size-[85%] (width AND height 85%),
    // leaving a 15% gap on the right and a 15% strip at the bottom showing the background.
    return (
      <div className="relative flex size-full flex-col">
        <div className="size-[85%] overflow-hidden">
          <SpeakerAvatar imgUrl={imgUrl} />
        </div>
        <div className="absolute bottom-0 right-0 flex h-[15%]">
          <StaticWaveform color={waveColor} />
        </div>
        {speakerName && (
          <div className="absolute bottom-0 left-[2%] w-[90%] origin-right rotate-90">
            <div className="truncate leading-tight" style={{ fontSize: '1.1em', color: nameColor }}>
              {speakerName}
            </div>
            {speakerTitle && (
              <div className="truncate leading-tight opacity-70" style={{ fontSize: '0.7em', color: titleColor }}>
                {speakerTitle}
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  // PORTRAIT — name left-bottom, waveform right-bottom
  return (
    <div className="flex size-full flex-col">
      <div className="w-full overflow-hidden" style={{ height: '70%' }}>
        <SpeakerAvatar imgUrl={imgUrl} />
      </div>
      <div className="flex items-end justify-between px-[3%] pb-[3%]" style={{ height: '30%' }}>
        <div className="flex min-w-0 flex-col">
          <div className="truncate leading-tight" style={{ fontSize: '1.1em', color: nameColor }}>
            {speakerName}
          </div>
          {speakerTitle && (
            <div className="truncate leading-tight opacity-70" style={{ fontSize: '0.7em', color: titleColor }}>
              {speakerTitle}
            </div>
          )}
        </div>
        <div className="h-2/5 w-[25%] shrink-0">
          <StaticWaveform color={waveColor} />
        </div>
      </div>
    </div>
  );
}
